import { useState, useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Calculator, Clock, DollarSign, TrendingUp, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PLANS } from "@/lib/plans";
import { useTranslation } from "@/i18n";

const ROICalculator = () => {
  const { t } = useTranslation();
  const [productCount, setProductCount] = useState(200);
  const [avgPrice, setAvgPrice] = useState(50);

  const calculations = useMemo(() => {
    const minutesPerProduct = 15;
    const hoursSaved = Math.round((productCount * minutesPerProduct) / 60);
    const copywriterCostPerProduct = 5;
    const copywriterTotal = productCount * copywriterCostPerProduct;

    // Potential revenue from better SEO
    const monthlyVisitsPerProduct = 10;
    const conversionLift = 0.02;
    const potentialRevenue = Math.round(productCount * avgPrice * monthlyVisitsPerProduct * conversionLift);

    // Find recommended plan
    const recommended = PLANS.find(p => p.creditsMonthly >= productCount && p.priceMonthly > 0) || PLANS[PLANS.length - 1];
    const rankaroCost = recommended.priceMonthly;
    const savings = copywriterTotal - rankaroCost + potentialRevenue;
    const roi = rankaroCost > 0 ? Math.round((savings / rankaroCost) * 100) : 0;

    return { hoursSaved, copywriterTotal, rankaroCost, savings, roi, potentialRevenue, recommendedPlan: recommended.name };
  }, [productCount, avgPrice]);

  return (
    <section className="py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="glow-orb glow-orb-primary w-[600px] h-[600px] top-0 left-1/4 opacity-15" />
        <div className="glow-orb glow-orb-accent w-[400px] h-[400px] bottom-0 right-0 opacity-10" />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm font-medium mb-6">
            <Calculator size={16} className="text-primary" />
            <span className="text-foreground">{t('roiCalculator.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t('roiCalculator.title')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('roiCalculator.subtitle')}
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Sliders */}
            <div className="p-5 md:p-8 rounded-2xl glass-card border border-border/30 space-y-8">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="text-sm font-medium text-foreground">
                    {t('roiCalculator.productsLabel')}
                  </label>
                  <span className="text-2xl font-bold text-primary">{productCount}</span>
                </div>
                <Slider
                  value={[productCount]}
                  onValueChange={([v]) => setProductCount(v)}
                  min={50}
                  max={5000}
                  step={50}
                  className="w-full"
                  aria-label={t('roiCalculator.productsLabel')}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>50</span>
                  <span>5,000</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="text-sm font-medium text-foreground">
                    {t('roiCalculator.avgPriceLabel')}
                  </label>
                  <span className="text-2xl font-bold text-primary">${avgPrice}</span>
                </div>
                <Slider
                  value={[avgPrice]}
                  onValueChange={([v]) => setAvgPrice(v)}
                  min={5}
                  max={500}
                  step={5}
                  className="w-full"
                  aria-label={t('roiCalculator.avgPriceLabel')}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>$5</span>
                  <span>$500</span>
                </div>
              </div>

              <div className="pt-4 border-t border-border/30">
                <p className="text-sm text-muted-foreground">
                  {t('roiCalculator.recommendedPlan')}: <span className="font-semibold text-primary">{calculations.recommendedPlan}</span>
                </p>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4">
              <div className="p-5 rounded-xl glass-card border border-border/30 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">{t('roiCalculator.timeSaved')}</p>
                  <p className="text-2xl font-bold text-foreground transition-all duration-300">
                    {calculations.hoursSaved} {t('roiCalculator.hours')}
                  </p>
                </div>
              </div>

              <div className="p-5 rounded-xl glass-card border border-border/30 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-destructive" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">{t('roiCalculator.copywriterCost')}</p>
                  <p className="text-2xl font-bold text-foreground line-through opacity-60 transition-all duration-300">
                    ${calculations.copywriterTotal.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="p-5 rounded-xl glass-card border border-border/30 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-accent" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">{t('roiCalculator.withRankaro')}</p>
                  <p className="text-2xl font-bold text-accent transition-all duration-300">
                    ${calculations.rankaroCost}/{t('roiCalculator.month')}
                  </p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mt-2 text-center">
                {t('roiCalculator.note')}
              </p>

              <Link to="/auth">
                <Button variant="cta" size="xl" className="w-full group mt-2">
                  {t('roiCalculator.cta')}
                  <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
